// src/modules/cache-bus.ts — S-91b/E4 (05.10.2026): „dane domeny się zmieniły”.
//
// Backend podbija wersję domeny (`cache_rev`: zam, faktury, oferty, katalog, porzadek, gs1,
// sprawy, wiadomosci, uzytkownicy, konfig) przy COMMIT każdego zapisu — z dowolnego procesu,
// workera synchronizacji czy psql — i rozsyła ją przez SSE (`sse-client.ts` → window
// `sb:cache`). Moduły liczników słuchają tu swoich domen i pobierają świeże liczby.
// Tylko liczniki: list nie przerysowujemy (zasada „No re-render on save”).
// Bez efektów ubocznych przy imporcie (testy bundlujące moduły nie odpalają SSE).

export const CACHE_EVENT = 'sb:cache';

export interface CacheDetail {
  domena: string;
  rev: number;
  firma?: string;
  resync?: boolean;
}

export interface OnCacheOpcje {
  /** Po powrocie do karty jedno odpalenie zaległych zdarzeń (domyślnie true). Moduły z własnym
   *  `visibilitychange` dają false, żeby nie pobierać dwa razy. */
  poPowrocie?: boolean;
  /** Najdłuższe czekanie przy serii zdarzeń (domyślnie 4× debounce). */
  maxWaitMs?: number;
  /** Najmniejszy odstęp między wywołaniami handlera (domyślnie 5 s). Przy ciągłej zmianie danych
   *  (synchronizacja marketplace'u co sekundę) karta pyta serwer najwyżej raz na tyle — nie zalewa go. */
  minOdstepMs?: number;
}

/**
 * Wywołuje `handler(domeny)` po zmianie którejś z `domeny` (z debounce). Karta ukryta: zdarzenia
 * czekają do powrotu. Zwraca funkcję odsubskrybowania.
 */
export function onCache(
  domeny: readonly string[],
  handler: (domeny: string[]) => void,
  debounceMs = 500,
  opcje: OnCacheOpcje = {},
): () => void {
  const chce = new Set(domeny);
  const zebrane = new Set<string>();
  const maxWait = opcje.maxWaitMs ?? debounceMs * 4;
  const minOdstep = Math.max(0, opcje.minOdstepMs ?? 5000);
  const poPowrocie = opcje.poPowrocie !== false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pierwszeOd = 0;
  let ostatnieWywolanie = 0;
  let zalegle = false;
  const ukryta = (): boolean => typeof document !== 'undefined' && document.visibilityState === 'hidden';

  const odpal = (): void => {
    timer = null;
    pierwszeOd = 0;
    if (ukryta()) {
      zalegle = true;
      return;
    }
    const lista = Array.from(zebrane);
    zebrane.clear();
    if (lista.length) {
      ostatnieWywolanie = Date.now();
      try {
        handler(lista);
      } catch (e) {
        console.warn('[cache-bus] handler:', e);
      }
    }
  };
  const zaplanuj = (ms: number): void => {
    const teraz = Date.now();
    if (!pierwszeOd) pierwszeOd = teraz;
    if (timer) clearTimeout(timer);
    const zaDebounce = teraz - pierwszeOd >= maxWait ? 0 : ms;
    // Ogon throttle: najwcześniej `minOdstep` po poprzednim wywołaniu (ostatnie zdarzenie serii nie ginie).
    const zaOdstep = ostatnieWywolanie ? Math.max(0, ostatnieWywolanie + minOdstep - teraz) : 0;
    timer = setTimeout(odpal, Math.max(zaDebounce, zaOdstep));
  };
  const naZdarzenie = (ev: Event): void => {
    const d = (ev as CustomEvent<CacheDetail>).detail;
    if (!d || typeof d.domena !== 'string') return;
    if (d.domena === '*') domeny.forEach((x) => zebrane.add(x));
    else if (chce.has(d.domena)) zebrane.add(d.domena);
    else return;
    if (ukryta()) {
      zalegle = true;
      return;
    }
    // Resync po przerwie strumienia (restart serwera): wszystkie karty naraz — rozrzut do 3 s.
    zaplanuj(d.resync ? debounceMs + Math.floor(Math.random() * 3000) : debounceMs);
  };
  const naWidocznosc = (): void => {
    if (ukryta() || !zalegle) return;
    zalegle = false;
    if (poPowrocie) zaplanuj(0);
    else zebrane.clear();
  };
  window.addEventListener(CACHE_EVENT, naZdarzenie);
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', naWidocznosc);
  return () => {
    if (timer) clearTimeout(timer);
    window.removeEventListener(CACHE_EVENT, naZdarzenie);
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', naWidocznosc);
  };
}
